import { createContext, useContext, useMemo, useState } from "react";

import { AbstractShop } from "~/shared/shipping/abstract-shop.interface";
import cartUtils from "~/utils/cart-utils";

type CheckoutStep = "shipping" | "billing" | "payment";

const Context = createContext<{
  step: CheckoutStep;
  setStep: (value: CheckoutStep) => void;
  cart: EVA.Core.ShoppingCartResponse | undefined;
  isCartLoading: boolean;
  productsParentsData?: Record<string, any>;
  orderSummaryOpen?: boolean;
  openOrderSummary: (value: boolean) => void;
  showStockDetails: boolean;
  openStockDetails: (value: boolean) => void;
  storeRef?: AbstractShop;
  setStoreRef: (store: AbstractShop) => void;
}>({
  step: "shipping",
  setStep: () => {},
  cart: undefined,
  isCartLoading: true,
  orderSummaryOpen: false,
  openOrderSummary: () => {},
  showStockDetails: false,
  openStockDetails: () => {},
  setStoreRef: () => {},
});

export default function CartProvider(props: {
  children: React.ReactNode;
  cart: EVA.Core.ShoppingCartResponse;
  isCartLoading: boolean;
  productsParentsData?: Record<string, any>;
  storeHasStock?: boolean;
}) {
  const [step, setStep] = useState<CheckoutStep>(() => {
    // if the cart has no delivery it has virtual product or has set store pickup as shipping method
    if (!props.cart.ShoppingCart.HasDelivery && (cartUtils.hasOnlyVirtualProducts(props.cart) || props.storeHasStock)) {
      // Start from the billing step if the order has no delivery
      return "billing";
    }
    return "shipping";
  });

  const [orderSummaryOpen, setOrderSummaryOpen] = useState<boolean>(false);
  const openOrderSummary = (open: boolean) => {
    setOrderSummaryOpen(open);
  };
  const [showStockDetails, setShowStockDetails] = useState(false);
  const openStockDetails = (open: boolean) => {
    setShowStockDetails(open);
  };
  const [storeRef, setStoreRef] = useState<AbstractShop | undefined>();

  return (
    <Context.Provider
      value={{
        step,
        setStep,
        cart: props.cart,
        isCartLoading: props.isCartLoading,
        productsParentsData: props.productsParentsData,
        orderSummaryOpen,
        openOrderSummary,
        showStockDetails,
        openStockDetails,
        storeRef,
        setStoreRef,
      }}
    >
      {props.children}
    </Context.Provider>
  );
}

export function useCartContext() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useCartContext must be used within a CartProvider`);
  }

  return context;
}

export function useGuaranteedCart() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useGuaranteedCart must be used within a CartProvider`);
  }

  if (!context.cart) {
    throw new Error(`useGuaranteedCart must be used when a cart is available`);
  }

  return context.cart!;
}

export function useGuaranteedCartWithParentsData() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useGuaranteedCart must be used within a CartProvider`);
  }

  if (!context.cart) {
    throw new Error(`useGuaranteedCart must be used when a cart is available`);
  }

  return { cart: context.cart!, cartParentsData: context.productsParentsData || {} };
}

export function useOrderSummaryContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(`useOrderSummaryContext must be used within a CartProvider`);
  }
  return {
    openOrderSummary: context.orderSummaryOpen,
    setOpenOrderSummary: context.openOrderSummary,
    showStockDetails: context.showStockDetails,
    openStockDetails: context.openStockDetails,
    storeRef: context.storeRef,
    setStoreRef: context.setStoreRef,
  };
}
